import { Link } from 'react-router-dom';
import { Monitor, Users, BookOpen, Headphones, Calendar, Clock, CheckCircle, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const deliveryFeatures = [
  {
    icon: Video,
    title: 'Live Virtual Sessions',
    description: 'Real-time instructor-led training via Zoom or Microsoft Teams with interactive participation'
  },
  {
    icon: Users,
    title: 'Small Batch Sizes',
    description: 'Maximum 20 participants per batch ensuring personalized attention and engagement'
  },
  {
    icon: BookOpen,
    title: 'Hands-On Labs',
    description: 'Practice on actual ERP systems with guided exercises and real-world scenarios'
  },
  {
    icon: Headphones,
    title: 'Instructor Support',
    description: 'Direct access to instructors for questions, doubt clearing, and guidance'
  },
  {
    icon: Calendar,
    title: 'Flexible Scheduling',
    description: 'Weekday and weekend batches available to suit your schedule'
  },
  {
    icon: Clock,
    title: 'Recording Access',
    description: 'All sessions recorded and available for revision and catch-up'
  },
];

const learningJourney = [
  {
    step: '01',
    title: 'Pre-Program',
    items: ['Program orientation', 'Learning platform access', 'Pre-reading materials', 'System setup guidance']
  },
  {
    step: '02',
    title: 'Core Training',
    items: ['Live instructor sessions', 'Hands-on lab exercises', 'Real-world use cases', 'Q&A and discussions']
  },
  {
    step: '03',
    title: 'Practice & Projects',
    items: ['Implementation projects', 'Case study analysis', 'Peer collaboration', 'Progress assessments']
  },
  {
    step: '04',
    title: 'Certification Prep',
    items: ['Exam strategies', 'Mock tests', 'Review sessions', 'Study materials']
  },
];

export default function TrainingModelPage() {
  return (
    <div className="min-h-screen pt-24" data-testid="training-model-page">
      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-brand-50 via-white to-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div
              
              
            >
              <span className="inline-block px-4 py-2 bg-brand-100 text-brand-700 rounded-full text-sm font-medium mb-6">
                Training Delivery Model
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold text-brand-900 mb-6">
                100% Live, Instructor-Led Online Training
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                Experience immersive, interactive learning with expert instructors. 
                Our live online format combines the benefits of classroom training 
                with the flexibility of remote learning.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/webinar">
                  <Button className="btn-primary" data-testid="experience-session-btn">
                    Experience a Free Session
                  </Button>
                </Link>
                <Link to="/programs">
                  <Button variant="outline" className="btn-secondary">
                    View Programs
                  </Button>
                </Link>
              </div>
            </div>
            
            <div
              
              
              
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1758518727707-b023e285b709?w=800&h=600&fit=crop" 
                  alt="Online training session"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Stats */}
      <section className="py-12 bg-white border-y border-slate-100">
        <div className="container-main">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { number: '100%', label: 'Live Training' },
              { number: '20', label: 'Max Batch Size' },
              { number: '24/7', label: 'Lab Access' },
              { number: '1:1', label: 'Doubt Sessions' },
            ].map((stat, index) => (
              <div
                key={stat.label}
                
                
                
                
                className="text-center"
              >
                <p className="text-3xl lg:text-4xl font-bold text-brand-700 mb-1">{stat.number}</p>
                <p className="text-slate-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Delivery Features */}
      <section className="py-20 lg:py-28">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-4">How We Deliver Training</h2>
            <p className="section-subheading mx-auto">
              Our training methodology is designed for maximum engagement and practical skill development
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {deliveryFeatures.map((feature, index) => (
              <div
                key={feature.title}
                
                
                
                
              >
                <Card className="program-card h-full">
                  <CardContent className="pt-6">
                    <div className="w-14 h-14 bg-brand-100 rounded-xl flex items-center justify-center mb-4">
                      <feature.icon className="w-7 h-7 text-brand-700" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-brand-900 mb-2">{feature.title}</h3>
                    <p className="text-slate-600 text-sm">{feature.description}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Journey */}
      <section className="py-20 lg:py-28 bg-brand-50/50">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-4">Your Learning Journey</h2>
            <p className="section-subheading mx-auto">
              A structured approach to help you progress from fundamentals to certification
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {learningJourney.map((phase, index) => (
              <div
                key={phase.step}
                
                
                
                
              >
                <Card className="program-card h-full relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-brand-500 to-brand-700" />
                  <CardContent className="pt-8">
                    <span className="text-5xl font-bold text-brand-100">{phase.step}</span>
                    <h3 className="font-heading font-semibold text-lg text-brand-900 mb-4 -mt-2">{phase.title}</h3>
                    <ul className="space-y-2">
                      {phase.items.map((item, i) => (
                        <li key={i} className="flex items-start space-x-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-600">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="section-heading mb-6">What Makes Our Training Different</h2>
              <div className="space-y-6">
                {[
                  { title: 'Expert Instructors', desc: 'Learn from certified professionals with real implementation experience' },
                  { title: 'Interactive Format', desc: 'Engage in discussions, ask questions, and collaborate with peers' },
                  { title: 'Practical Focus', desc: 'Work on real scenarios, not just theory - gain job-ready skills' },
                  { title: 'Continuous Support', desc: 'Get help beyond class hours through dedicated support channels' },
                  { title: 'Career Guidance', desc: 'Receive resume review, interview prep, and job search support' },
                ].map((item, index) => (
                  <div
                    key={item.title}
                    
                    
                    
                    
                    className="flex items-start space-x-4"
                  >
                    <div className="w-8 h-8 bg-brand-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-brand-700" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-brand-900">{item.title}</h3>
                      <p className="text-sm text-slate-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1758518731468-98e90ffd7430?w=600&h=500&fit=crop" 
                alt="Team collaboration"
                className="rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-brand-700">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Experience Our Training First-Hand
            </h2>
            <p className="text-lg text-brand-100 mb-8">
              Join a free webinar to see how our live training works. 
              Get a taste of our teaching style and ask any questions.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/webinar">
                <Button className="bg-white text-brand-700 hover:bg-brand-50 rounded-full px-8 py-3 font-medium" data-testid="join-webinar-btn">
                  Join Free Webinar
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 py-3 font-medium">
                  Schedule a Call
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
